import type { LobbyGame } from '../api/getLobby';

const ET_TIMEZONE = 'America/New_York';
const MS_IN_24_HOURS = 86_400_000;

/**
 * My Games card start line — always an exact calendar date (never "Soon" / "X Days").
 * Adds the ET start time once the contest is within 24 hours (before or after kickoff).
 */
export function formatMyGamesStartDisplay(
  startUtc: string | null | undefined,
  now = new Date()
): string {
  if (!startUtc?.trim()) return 'TBD';
  const start = new Date(startUtc);
  if (Number.isNaN(start.getTime())) return 'TBD';

  const datePart = new Intl.DateTimeFormat('en-US', {
    timeZone: ET_TIMEZONE,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(start);

  const msUntilStart = start.getTime() - now.getTime();
  if (msUntilStart > MS_IN_24_HOURS) return datePart;

  const timePart = new Intl.DateTimeFormat('en-US', {
    timeZone: ET_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
    .format(start)
    .replace(/\s/g, ' ')
    .trim();

  return `${datePart} @ ${timePart} ET`;
}

export function gameTypeLabel(type: string | null): string {
  if (!type) return '';
  const normalized = type.trim().toLowerCase();
  if (normalized === 'pickem') return "Pick'em";
  if (normalized === 'squares') return 'Squares';
  if (normalized === 'daily_reveal' || normalized === 'daily_draw') return 'Daily Draw';
  if (normalized === 'survivor') return 'Survivor';
  if (normalized === 'bingo') return 'Game Grid';
  return type;
}

export function sportLabel(sport: string | null): string {
  if (!sport) return '';
  return sport
    .trim()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/** "Picks Lock Apr 12 @ 1:35 PM ET" */
export function formatPicksLockEt(startUtc: string | null | undefined): string | null {
  if (!startUtc) return null;
  try {
    const date = new Date(startUtc);
    if (Number.isNaN(date.getTime())) return null;

    const monthDay = new Intl.DateTimeFormat('en-US', {
      timeZone: ET_TIMEZONE,
      month: 'short',
      day: 'numeric',
    }).format(date);

    const time = new Intl.DateTimeFormat('en-US', {
      timeZone: ET_TIMEZONE,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
      .format(date)
      .replace(/\s/g, ' ')
      .trim();

    return `Picks Lock ${monthDay} @ ${time} ET`;
  } catch {
    return null;
  }
}

function parseDollarAmount(text: string): number | null {
  const match = text.replace(/,/g, '').match(/\$\s*([\d]+(?:\.\d+)?)/);
  if (!match) return null;
  const amount = Number(match[1]);
  return Number.isFinite(amount) ? amount : null;
}

function formatDollarPrizePool(amount: number): string {
  const formatted =
    amount % 1 === 0
      ? amount.toLocaleString('en-US', { maximumFractionDigits: 0 })
      : amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `$${formatted} Prize Pool`;
}

/** Lobby cards: "$5,000 Prize Pool" (share-of-pool wording, comma-formatted). */
export function formatWinPrize(game: LobbyGame): string | null {
  const text = game.prize_text_en?.trim() || game.prize_text_fr?.trim();
  if (text) {
    const amount = parseDollarAmount(text);
    if (amount != null) {
      return formatDollarPrizePool(amount);
    }
    const withoutWin = text.replace(/^win\s+/i, '').trim();
    if (/prize\s+pool/i.test(withoutWin)) return withoutWin;
    return `${withoutWin} Prize Pool`;
  }

  if (typeof game.custom_prize_amount === 'number' && Number.isFinite(game.custom_prize_amount)) {
    return formatDollarPrizePool(game.custom_prize_amount);
  }

  return null;
}

export function formatEntryCount(count: number | null | undefined): string | null {
  if (count == null || !Number.isFinite(count) || count < 0) return null;
  const rounded = Math.floor(count);
  if (rounded === 0) return '0 Entries';
  const label = rounded === 1 ? 'Entry' : 'Entries';
  return `${rounded.toLocaleString('en-US')} ${label}`;
}

/** Large hero headline — contest title when set, otherwise game type. */
export function heroHeadline(game: LobbyGame): string {
  const title = game.title_en?.trim();
  if (title) return title.toUpperCase();
  const type = gameTypeLabel(game.game_type);
  if (type) return type.toUpperCase();
  return 'GAME';
}

export function heroSubtitle(game: LobbyGame): string | null {
  return game.title_fr?.trim() || null;
}

export function gameEntryCount(game: LobbyGame): number | null {
  if (typeof game.entry_count === 'number' && Number.isFinite(game.entry_count)) {
    return Math.max(0, Math.floor(game.entry_count));
  }
  if (typeof game.custom_entries === 'number' && Number.isFinite(game.custom_entries)) {
    return Math.max(0, Math.floor(game.custom_entries));
  }
  return null;
}
